import { LoginForm } from "@/components/auth/login-form";
import { TwoFactorVerification } from "@/components/auth/two-factor-verification";
import { LoginPageLayout } from "@/components/shadcn-studio/blocks/login-page-03/login-page-03";
import { getSafeRedirectUrl } from "@/lib/auth-config";
import { ensureOrgBootstrap } from "@/lib/org-bootstrap";
import { getPartnerSourceFromReturnTo } from "@/lib/partner-source";
import { organizationListQueryOptions } from "@/lib/queries/organization";
import { invalidateSession, sessionQueryOptions } from "@/lib/queries/session";
import { needsRoleSelection } from "@/lib/utils/organization";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";

import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  isRedirect,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

// Sanitize returnTo at parse time - consumers get a safe value or undefined
const searchSchema = z.object({
  returnTo: z
    .string()
    .optional()
    .transform((val) => {
      const safe = getSafeRedirectUrl(val);
      return safe === "/" ? undefined : safe;
    })
    .catch(undefined),
  // 2FA redirect from OAuth flow
  twoFactorRedirect: z.coerce.boolean().optional().catch(undefined),
});

export const Route = createFileRoute("/(auth)/login")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, search }) => {
    try {
      const session = await context.queryClient.fetchQuery(
        sessionQueryOptions(),
      );

      // Redirect authenticated users to their destination
      if (session?.user && session?.session) {
        // If user has a specific destination, bootstrap org before redirect
        // so that API calls at the destination don't fail. See EPD-6857.
        if (search.returnTo) {
          try {
            await ensureOrgBootstrap(context.queryClient);
          } catch (err) {
            console.warn(
              "Org bootstrap failure in beforeLoad redirect — falling back to /",
              err,
            );
            throw redirect({ to: "/" });
          }
          throw redirect({ to: search.returnTo });
        }

        // Check if user is a member of any organization (force fresh fetch)
        try {
          const organizations = await context.queryClient.fetchQuery({
            ...organizationListQueryOptions(),
            staleTime: 0,
          });

          if (!organizations || organizations.length === 0) {
            // New user with no orgs — shouldn't happen since
            // ensureUserHasOrganization runs on login, but fallback to home
            throw redirect({ to: "/" });
          }

          // Bare orgs (auto-created, no organizationType) → role selection
          // Only auto-redirect when there is exactly one bare org to avoid
          // picking an arbitrary org when multiple bare orgs exist.
          const bareOrgs = organizations.filter((o) =>
            needsRoleSelection(o.metadata as string | null),
          );
          if (bareOrgs.length === 1) {
            throw redirect({
              to: "/o/$orgSlug/select-role",
              params: { orgSlug: bareOrgs[0].slug },
            });
          }

          if (organizations.length === 1) {
            throw redirect({
              to: "/o/$orgSlug",
              params: { orgSlug: organizations[0].slug },
            });
          }
        } catch (error) {
          // Re-throw redirects, ignore org fetch errors
          if (isRedirect(error)) throw error;
        }

        // Default: redirect to home (org picker)
        throw redirect({ to: "/" });
      }
    } catch (error) {
      // Re-throw redirects, show login form for fetch errors
      if (isRedirect(error)) throw error;
    }
  },
  component: LoginPage,
});

export interface HandleLoginSuccessDeps {
  queryClient: QueryClient;
  router: {
    invalidate: () => Promise<void>;
    navigate: (opts: {
      to: string;
      params?: Record<string, string>;
    }) => Promise<void>;
  };
  returnTo: string | undefined;
}

export async function handleLoginSuccess(deps: HandleLoginSuccessDeps) {
  const { queryClient, router, returnTo } = deps;
  await invalidateSession(queryClient);
  // Note: router.invalidate() is intentionally NOT called here for the returnTo path.
  // Calling it before org bootstrap would trigger the login beforeLoad to redirect
  // to returnTo immediately (user is now authenticated + returnTo is set), before
  // createAuto has been called — causing resolveByProgramId to see orgs.length === 0
  // → NOT_FOUND ("Campaign not found"). See EPD-6857.

  // If user was trying to go somewhere specific, ensure they have an org+profile
  // before navigating so that API calls at the destination don't fail, and so the
  // onboarding wizard doesn't intercept them mid-flow.
  if (returnTo) {
    try {
      await ensureOrgBootstrap(queryClient);
    } catch (err) {
      console.warn(
        "Org bootstrap failure during login redirect — falling back to /",
        err,
      );
      await router.navigate({ to: "/" });
      return;
    }

    await router.navigate({ to: returnTo });
    return;
  }

  // For non-returnTo navigation, invalidate router to pick up new session state
  await router.invalidate();

  // Check if user is a member of any organization (force fresh fetch)
  try {
    const organizations = await queryClient.fetchQuery({
      ...organizationListQueryOptions(),
      staleTime: 0,
    });

    if (!organizations || organizations.length === 0) {
      // Shouldn't happen — ensureUserHasOrganization runs on login
      await router.navigate({ to: "/" });
      return;
    }

    // Bare orgs (auto-created, no organizationType) → role selection
    const bareOrgs = organizations.filter((o) =>
      needsRoleSelection(o.metadata as string | null),
    );
    if (bareOrgs.length === 1) {
      await router.navigate({
        to: "/o/$orgSlug/select-role",
        params: { orgSlug: bareOrgs[0].slug },
      });
      return;
    }

    if (organizations.length === 1) {
      await router.navigate({
        to: "/o/$orgSlug",
        params: { orgSlug: organizations[0].slug },
      });
      return;
    }
  } catch {
    // If org fetch fails, fall through to home
  }

  // Default: go to home (org picker)
  await router.navigate({ to: "/" });
}

function LoginPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [error, setError] = useState("");

  // Show 2FA verification if redirected from OAuth with 2FA required
  const [show2FA, setShow2FA] = useState(search.twoFactorRedirect === true);
  // Partner-sourced connect flow (EPD-11850): the source rides inside returnTo
  const partner = getPartnerSourceFromReturnTo(search.returnTo);

  async function handleSuccess() {
    await handleLoginSuccess({
      queryClient,
      router,
      returnTo: search.returnTo,
    });
  }

  const handle2FACancel = () => {
    setShow2FA(false);
    setError("");
    // Clear the twoFactorRedirect param from URL
    router.navigate({ to: "/login", search: { returnTo: search.returnTo } });
  };

  return (
    <LoginPageLayout>
      {show2FA ? (
        <Card className="w-full border-none shadow-none">
          <CardHeader className="px-0">
            <CardTitle>Two-Factor Authentication</CardTitle>
            <CardDescription>
              Enter the verification code from your authenticator app to
              complete sign in.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            <TwoFactorVerification
              onSuccess={handleSuccess}
              onError={setError}
              onCancel={handle2FACancel}
            />
            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Verification failed</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          <LoginForm onSuccess={handleSuccess} />
          {partner && (
            <p className="text-center text-sm text-muted-foreground">
              After you sign in, you'll connect your social accounts and return
              to {partner.name}.
            </p>
          )}
        </div>
      )}
    </LoginPageLayout>
  );
}
